import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, Lock, MoreHorizontal } from "lucide-react";
import Navbar from "../components/Navbar";
import Avatar from "../components/Avatar";
import ServiceBadges from "../components/ServiceBadges";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTop8 } from "../context/Top8Context";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

const Header = ({ children }) => (
  <h2 className="social-head"><span className="social-star">★</span>{children}<span className="social-star">★</span></h2>
);
const Divider = () => <div className="glitter-divider my-4" aria-hidden />;

export default function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { removeFromTop8 } = useTop8();
  const { skin } = useTheme();
  const isVideostore = skin === "videostore";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/profile/${username}`)
      .then(({ data }) => setData(data))
      .catch((e) => setData({ error: e.response?.status === 404 ? "Profile not found." : "Could not load profile." }))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(path, { username, ...body });
      if (okMsg) toast.success(okMsg);
      window.dispatchEvent(new Event("dial-notify-refresh"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const doBlock = async () => {
    setMenu(false);
    if (!window.confirm(`Block ${data.profile.name}? This removes any friendship and hides you from each other.`)) return;
    setBusy(true);
    try {
      await api.post("/friends/block", { username });
      toast("User blocked");
      navigate("/friends");
    } catch { toast.error("Could not block."); setBusy(false); }
  };

  const renderAction = () => {
    const s = data.rel_state;
    if (s === "self") return <Link to="/settings" className="social-pill ghost" data-testid="profile-edit-link">Edit My Profile</Link>;
    if (s === "friends") return <button disabled={busy} onClick={() => window.confirm(`Remove ${data.profile.name} from friends?`) && act("/friends/remove", {}, "Friend removed")} className="social-pill" data-testid="profile-action-button">Friends ✓</button>;
    if (s === "outgoing") return <button disabled={busy} onClick={() => act("/friends/cancel", {}, "Request cancelled")} className="social-pill ghost" data-testid="profile-action-button">Request Sent — Cancel</button>;
    if (s === "incoming") return (
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => act("/friends/accept", {}, "You're now friends ★")} className="social-pill" data-testid="profile-action-button">Accept Request</button>
        <button disabled={busy} onClick={() => act("/friends/decline", {}, "Request declined")} className="social-pill ghost" data-testid="profile-decline-button">Decline</button>
      </div>
    );
    return <button disabled={busy} onClick={() => act("/friends/request", {}, "Friend request sent ★")} className="social-pill" data-testid="profile-action-button">Add to Friends</button>;
  };

  return (
    <div className="social-shell" data-skin="ancientsocial" data-variant="profile" data-testid="profile-page">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        {loading ? (
          <p className="social-panel p-6 text-center text-[var(--text-secondary)]">Loading profile…</p>
        ) : data?.error ? (
          <div className="social-panel p-8 text-center" data-testid="profile-error">
            <p className="font-bold text-[var(--text-primary)]">{data.error}</p>
            <Link to="/friends" className="social-link mt-3 inline-block">← Back to Friends</Link>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
            {/* Sidebar */}
            <aside className="space-y-5">
              {isVideostore && <MemberCard profile={data.profile} />}
              <section className="social-panel" data-testid="profile-card">
                <Header>{data.profile.name}'s Profile</Header>
                <div className="flex items-center gap-3 p-3">
                  <div className="social-avatar-frame"><Avatar user={data.profile} size={72} rounded="rounded-[3px]" testid="profile-avatar-img" /></div>
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[var(--text-primary)]" data-testid="profile-name">{data.profile.name}</p>
                    <p className="text-xs text-[var(--text-muted)]">@{data.profile.username}</p>
                    <p className="social-online"><span className="online-dot" /> ONLINE NOW</p>
                  </div>
                </div>
                {data.profile.mood && <p className="px-3 pb-2 text-sm italic text-[var(--text-secondary)]" data-testid="profile-mood">Mood: <span className="text-[var(--accent)]">{data.profile.mood}</span></p>}
                <ul className="space-y-1 px-3 pb-3 text-sm text-[var(--text-secondary)]">
                  <li>★ Friends: <b>{data.friend_count}</b></li>
                  <li>★ Visibility: <b className="capitalize">{data.visibility.replace("_", " ")}</b></li>
                </ul>
                <div className="mx-3 mb-3 rounded border border-dotted border-[var(--border-subtle)] bg-black/80 p-2 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--glitter-cyan)]">Visitors</p>
                  <p className="visitor-counter" data-testid="profile-visitor-counter">{String(data.visits).padStart(6, "0").split("").map((d, i) => <span key={i} className="odo-digit">{d}</span>)}</p>
                </div>
                <div className="flex flex-col gap-2 px-3 pb-3">
                  {renderAction()}
                  <button onClick={() => toast("Messages are coming soon ✉")} className="social-link text-left text-sm" data-testid="profile-send-message">» Send Message</button>
                  {data.rel_state !== "self" && (
                    <div className="relative">
                      <button onClick={() => setMenu((o) => !o)} className="social-link flex items-center gap-1 text-sm" data-testid="profile-more-menu"><MoreHorizontal size={14} /> More</button>
                      {menu && (
                        <div className="absolute z-20 mt-1 w-40 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-1 shadow-lg">
                          <button onClick={doBlock} className="w-full rounded px-3 py-2 text-left text-sm text-[var(--accent)] hover:bg-black/5" data-testid="profile-block-button">Block user</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </aside>

            {/* Main */}
            <div className="min-w-0 space-y-5">
              {data.access === "private" ? (
                <section className="social-panel p-10 text-center" data-testid="profile-private-notice">
                  <Lock size={28} className="mx-auto text-[var(--text-muted)]" />
                  <p className="mt-3 font-bold text-[var(--text-primary)]">This profile is private</p>
                  <p className="text-sm text-[var(--text-secondary)]">Add {data.profile.name} as a friend to see their Top 8 and favorites.</p>
                </section>
              ) : data.access === "limited" ? (
                <section className="social-panel p-10 text-center" data-testid="profile-limited-notice">
                  <Lock size={28} className="mx-auto text-[var(--text-muted)]" />
                  <p className="mt-3 font-bold text-[var(--text-primary)]">Friends only</p>
                  <p className="text-sm text-[var(--text-secondary)]">{data.profile.name}'s Top 8 and favorites are visible to friends. Send a request to connect.</p>
                </section>
              ) : (
                <>
                  <section className="social-panel" data-testid="profile-top8">
                    <Divider />
                    <Header>{data.profile.name}'s Top 8</Header>
                    <div className="grid grid-cols-4 gap-x-2 gap-y-3 p-3 sm:grid-cols-4 sm:gap-4 sm:p-4">
                      {buildTop8(data).map((slot, i) => (
                        <div key={i} className="group relative min-w-0" data-testid={`profile-top8-slot-${i}`}>
                          <span className="social-rank">{i + 1}</span>
                          {slot ? (
                            <>
                              <button onClick={() => navigate(`/title/${slot.id}`)} className="block w-full overflow-hidden rounded border border-dotted border-[var(--border-subtle)] bg-white">
                                <div className="aspect-[2/3] overflow-hidden"><img src={slot.poster || slot.img} alt={slot.title} className="h-full w-full object-cover" /></div>
                              </button>
                              <p className="mt-1.5 truncate text-[11px] sm:text-sm font-bold text-[var(--text-primary)]" title={slot.title}>{slot.title}</p>
                              <ServiceBadges id={slot.id} className="mt-1 w-fit" />
                              {data.rel_state === "self" && (
                                <button
                                  onClick={() => { removeFromTop8(slot); setData((d) => ({ ...d, top8: d.top8.filter((x) => x.id !== slot.id) })); }}
                                  className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[var(--accent)]"
                                  data-testid={`profile-top8-remove-${slot.id}`} title="Remove from Top 8">✕</button>
                              )}
                            </>
                          ) : (
                            <div className="flex aspect-[2/3] items-center justify-center rounded border border-dashed border-[var(--border-subtle)] text-[11px] text-[var(--text-muted)]">#{i + 1}<br />empty</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  {data.favorites?.length > 0 && (
                    <section className="social-panel" data-testid="profile-favorites">
                      <Header>Favorites</Header>
                      <div className="flex gap-3 overflow-x-auto p-3">
                        {data.favorites.map((it) => (
                          <button key={it.id} onClick={() => navigate(`/title/${it.id}`)} className="w-28 flex-none text-left">
                            <div className="aspect-[2/3] overflow-hidden rounded border border-dotted border-[var(--border-subtle)] bg-white"><img src={it.poster || it.img} alt={it.title} className="h-full w-full object-cover" /></div>
                            <p className="mt-1 truncate text-xs font-bold text-[var(--text-primary)]">{it.title}</p>
                          </button>
                        ))}
                      </div>
                    </section>
                  )}

                  {data.friends_preview?.length > 0 && (
                    <section className="social-panel" data-testid="profile-friends-space">
                      <Header>{data.profile.name}'s Friend Space</Header>
                      <p className="px-4 pt-1 text-xs text-[var(--text-muted)]">{data.friend_count} {data.friend_count === 1 ? "Friend" : "Friends"}</p>
                      <div className="grid grid-cols-4 gap-x-4 gap-y-5 p-4 sm:grid-cols-8 sm:gap-3">
                        {data.friends_preview.map((f) => (
                          <Link key={f.id} to={`/profile/${f.username}`} className="group block text-center" data-testid={`profile-friend-${f.username}`}>
                            <div className="social-avatar-frame mx-auto w-fit"><Avatar user={f} size={64} rounded="rounded-[3px]" /></div>
                            <p className="mt-1.5 truncate text-xs font-bold text-[var(--accent)] group-hover:underline" title={f.name}>{f.name}</p>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MemberCard({ profile }) {
  const hash = [...(profile.username || profile.name || "dial")].reduce((a, c) => a + c.charCodeAt(0), 0);
  const memberNo = String((hash * 31) % 100000000).padStart(8, "0").replace(/(\d{4})(\d{4})/, "$1 $2");
  const since = profile.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;
  return (
    <section className="vs-member-card" data-testid="vs-member-card">
      <div className="vs-mc-band">
        <span className="vs-mc-brand">THE DIAL VIDEO</span>
        <span className="vs-mc-chip" aria-hidden="true" />
      </div>
      <p className="vs-mc-label">Member Card</p>
      <div className="vs-mc-row">
        <div className="vs-mc-avatar"><Avatar user={profile} size={46} rounded="rounded-[3px]" /></div>
        <div className="min-w-0">
          <p className="vs-mc-name" title={profile.name}>{profile.name}</p>
          <p className="vs-mc-sub">@{profile.username}</p>
        </div>
      </div>
      <div className="vs-mc-no"><span>Member No.</span><span>{memberNo}</span></div>
      {since && <div className="vs-mc-no" data-testid="vs-member-since"><span>Member Since</span><span>{since}</span></div>}
      <div className="vs-mc-barcode" aria-hidden="true" />
      <p className="vs-mc-foot">★ Valued Member · Rewinds Welcome ★</p>
    </section>
  );
}

function buildTop8(data) {
  const slots = [];
  for (const t of data.top8 || []) {
    if (slots.length >= 8) break;
    slots.push(t);
  }
  while (slots.length < 8) slots.push(null);
  return slots.slice(0, 8);
}
